import { useEffect, useMemo, useState } from 'react'
import ModulePanel from '../../../../../ui/ModulePanel/ModulePanel'
import { db } from '../../../../../config/firebase'
import getAvailableBanners from './getAvailableBanners'
import { mapBoBannersToHeroTiles, reorderHeroSeriesForMobile } from './mapBoBannersToHeroTiles'
import HeroCarouselFrame from './HeroCarouselFrame'
import './BannersSitePreview.sass'

/**
 * Live site Hero preview under the banner editor:
 * left = desktop (4 tiles), right = mobile (1 tile, series order 2→1).
 * Overlays unsaved form draft for the series currently being edited.
 */
function BannersSitePreview({ seriesId, seriesDraft }) {
  const [banners, setBanners] = useState(null)

  useEffect(() => {
    const unsubscribe = db.collection('settings/banners/list').onSnapshot((snap) => {
      const data = []
      snap.forEach((doc) => data.push({ id: doc.id, ...doc.data() }))
      setBanners(data)
    })
    return () => unsubscribe()
  }, [])

  const mergedBanners = useMemo(() => {
    if (!Array.isArray(banners)) return []
    if (!seriesId || !seriesDraft) return banners

    let found = false
    const next = banners.map((banner) => {
      if (banner.id !== seriesId) return banner
      found = true
      return {
        ...banner,
        ...seriesDraft,
        id: seriesId,
      }
    })

    if (!found) {
      next.unshift({ ...seriesDraft, id: seriesId })
    }

    return next
  }, [banners, seriesId, seriesDraft])

  const available = useMemo(() => getAvailableBanners(mergedBanners), [mergedBanners])
  const desktopTiles = useMemo(() => mapBoBannersToHeroTiles(available), [available])
  const mobileTiles = useMemo(() => reorderHeroSeriesForMobile(desktopTiles), [desktopTiles])

  // Stable fingerprint so typing in unrelated form fields does not remount swipe mid-gesture.
  const desktopKey = useMemo(
    () => desktopTiles.map((tile) => `${tile.id}:${tile.imageSrc}`).join('|'),
    [desktopTiles]
  )
  const mobileKey = useMemo(
    () => mobileTiles.map((tile) => `${tile.id}:${tile.imageSrc}`).join('|'),
    [mobileTiles]
  )

  return (
    <ModulePanel title="Прев’ю на сайті">
      <div className="BannersSitePreview">
        {banners == null ? (
          <p className="BannersSitePreview-Empty">Завантаження…</p>
        ) : (
          <div className="BannersSitePreview-Grid">
            <div className="BannersSitePreview-Frame BannersSitePreview-Frame_desktop">
              <p className="Title">Desktop</p>
              <div className="BannersSitePreview-FrameBody">
                <HeroCarouselFrame key={`desktop-${desktopKey}`} banners={desktopTiles} columns={4} />
              </div>
            </div>

            <div className="BannersSitePreview-Frame BannersSitePreview-Frame_mobile">
              <p className="Title">Mobile</p>
              <div className="BannersSitePreview-Phone">
                <HeroCarouselFrame key={`mobile-${mobileKey}`} banners={mobileTiles} columns={1} />
              </div>
            </div>
          </div>
        )}
      </div>
    </ModulePanel>
  )
}

export default BannersSitePreview
